import { Link } from "react-router-dom";
import { CalendarDays, Clock, MapPin, Users } from "lucide-react";
import { VehicleImagePreview } from "@/components/dailyCommute/VehicleImagePreview";
import { getCurrencySymbol } from "@/utils/currency";
import { ROUTE_PATHS } from "@/routes/routePaths";
import { FIND_RIDE_COLORS } from "../constants/findRide.constants";
import { tFindRide } from "../locales/FindRideTranslations";
import type { FindRideResultItem } from "../types/findRide.types";

type FindRideResultCardProps = {
  ride: FindRideResultItem;
};

function formatPrice(price: number | null, currency: string): string | null {
  if (price == null || !Number.isFinite(price)) return null;
  return `${getCurrencySymbol(currency)}${Math.round(price)}`;
}

export function FindRideResultCard({ ride }: FindRideResultCardProps) {
  const price = formatPrice(ride.ridePrice, ride.priceCurrency);
  const href = `${ROUTE_PATHS.RIDE_INFO}?rideId=${encodeURIComponent(ride.rideId)}`;

  return (
    <Link
      to={href}
      className="block overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm transition-shadow hover:shadow-md"
    >
      <div className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center sm:p-5">
        <VehicleImagePreview
          make={ride.vehicleMake}
          model={ride.vehicleModel}
          variant="thumbnail"
          showLabel
          className="self-center sm:self-auto"
        />

        <div className="min-w-0 flex-1 space-y-2.5">
          <div className="flex items-start justify-between gap-3">
            <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-base font-semibold text-gray-900">
              <MapPin className="h-4 w-4 shrink-0 text-[#22C55E]" />
              <span className="truncate">{ride.fromLabel}</span>
              <span className="text-gray-300">→</span>
              <span className="truncate">{ride.toLabel}</span>
            </div>

            {price ? (
              <div className="shrink-0 text-right">
                <p
                  className="text-xl font-bold"
                  style={{ color: FIND_RIDE_COLORS.pageGreen }}
                >
                  {price}
                </p>
                <p className="text-[11px] text-gray-400">
                  {tFindRide("perSeat")}
                </p>
              </div>
            ) : null}
          </div>

          <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-xs text-gray-500 sm:text-sm">
            {ride.startDate ? (
              <span className="inline-flex items-center gap-1">
                <CalendarDays className="h-3.5 w-3.5" />
                {ride.startDate}
              </span>
            ) : null}
            {ride.startTime ? (
              <span className="inline-flex items-center gap-1">
                <Clock className="h-3.5 w-3.5" />
                {ride.startTime}
              </span>
            ) : null}
            {ride.seatsAvailability != null ? (
              <span className="inline-flex items-center gap-1">
                <Users className="h-3.5 w-3.5" />
                {tFindRide("seatsLeft", { count: ride.seatsAvailability })}
              </span>
            ) : null}
          </div>
        </div>
      </div>
    </Link>
  );
}
